import React, { useState, useEffect } from "react";
import { Form, Button } from "semantic-ui-react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  subject_add,
  subject_edit,
  subject_update,
} from "../../../../apis/ClassManagement/Subjects.jsx";
import {
  levels_by_subject,
  level_add,
  level_update,
  level_destroy,
} from "../../../../apis/Level.jsx";
import { handleNavigation } from "../../../../Utils/Utils.js";

const AddSubject = () => {
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const onSubmit = async (data) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    let result = await subject_add(data);
    if (result.status) {
      return navigate("/admin/general-setting/subjects");
    }
    setIsSubmitting(false);
  };
  return (
    <div className="right-nav" id="dash-event">
      <div className="modal-content">
        <div className="modal-header">
          <h4 className="modal-title">Add Subject</h4>
          <Link
            className="edit-btn"
            to={`/admin/general-setting/subjects`}
            onClick={(e) => {
              handleNavigation(e, isDirty);
            }}
          >
            <i class="fa fa-arrow-left" aria-hidden="true"></i>
          </Link>
        </div>
        <div className="modal-body">
          <Form className="form-horizontal" onSubmit={handleSubmit(onSubmit)}>
            <div className="row">
              <div className="col-md-6">
                <div className="form-group">
                  <label>Subject *</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter Subject"
                    {...register("subject", { required: true })}
                  />
                  {errors.subject && errors.subject.type === "required" && (
                    <span className="validation-error">This is required</span>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label>Status</label>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      {...register("status", { required: true })}
                      id="inlineRadio1"
                      value="1"
                      defaultChecked
                    />
                    <label className="form-check-label" htmlFor="inlineRadio1">
                      Active
                    </label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      {...register("status", { required: true })}
                      id="inlineRadio2"
                      value="0"
                    />
                    <label className="form-check-label" htmlFor="inlineRadio2">
                      Inactive
                    </label>
                  </div>
                </div>
                {errors.status && errors.status.type === "required" && (
                  <span className="validation-error">This is required</span>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <Button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                Save
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

const EditSubject = () => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isDirty },
  } = useForm();
  const navigate = useNavigate();
  const params = useParams();
  const id = params;
  const subjectId = params?.id;
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Levels state (per-subject; stored in skill_type table).
  // `name` (skill column) is shown in admin dropdowns AND on the student portal.
  // `abbreviation` is kept for internal reference only — no longer rendered to students.
  const [levels, setLevels] = useState([]);
  const [newLevel, setNewLevel] = useState("");
  const [newLevelAbbr, setNewLevelAbbr] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [editingAbbr, setEditingAbbr] = useState("");
  const [levelsBusy, setLevelsBusy] = useState(false);

  const loadLevels = async () => {
    if (!subjectId) return;
    const res = await levels_by_subject(subjectId);
    setLevels(Array.isArray(res?.data) ? res.data : []);
  };

  const handleAddLevel = async (e) => {
    e.preventDefault();
    const name = newLevel.trim();
    const abbreviation = newLevelAbbr.trim();
    if (!name || levelsBusy) return;
    setLevelsBusy(true);
    const res = await level_add({ subject_id: subjectId, name, abbreviation });
    if (res?.status) {
      setNewLevel("");
      setNewLevelAbbr("");
      await loadLevels();
    }
    setLevelsBusy(false);
  };

  const handleStartEdit = (row) => {
    setEditingId(row.id);
    setEditingName(row.name);
    setEditingAbbr(row.abbreviation || "");
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingName("");
    setEditingAbbr("");
  };

  const handleSaveEdit = async () => {
    const name = editingName.trim();
    const abbreviation = editingAbbr.trim();
    if (!name || levelsBusy || editingId == null) return;
    setLevelsBusy(true);
    const res = await level_update(editingId, { name, abbreviation });
    if (res?.status) {
      setEditingId(null);
      setEditingName("");
      setEditingAbbr("");
      await loadLevels();
    }
    setLevelsBusy(false);
  };

  const handleDeleteLevel = async (rowId) => {
    if (levelsBusy) return;
    const ok = window.confirm("Delete this level? This cannot be undone.");
    if (!ok) return;
    setLevelsBusy(true);
    const res = await level_destroy(rowId);
    if (res?.status) await loadLevels();
    setLevelsBusy(false);
  };

  const onSubmit = async (data) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    let result = await subject_update(id, data);
    if (result.status) {
      return navigate("/admin/general-setting/subjects");
    }
    setIsSubmitting(false);
  };
  const fetchData = async () => {
    let res = await subject_edit(id);
    setValue("subject", res?.data[0]?.subject);
    setValue("status", res?.data[0]?.status.toString());
  };
  useEffect(() => {
    fetchData();
    loadLevels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="right-nav" id="dash-event">
      <div className="modal-content">
        <div className="modal-header">
          <h4 className="modal-title">Modify Subject</h4>
          <Link
            className="edit-btn"
            to={`/admin/general-setting/subjects`}
            onClick={(e) => handleNavigation(e, isDirty)}
          >
            <i class="fa fa-arrow-left" aria-hidden="true"></i>
          </Link>
        </div>
        <div className="modal-body">
          <Form className="form-horizontal" onSubmit={handleSubmit(onSubmit)}>
            <div className="row">
              <div className="col-md-6">
                <div className="form-group">
                  <label>Subject *</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter Subject"
                    {...register("subject", { required: true })}
                  />
                </div>
                {errors.subject && errors.subject.type === "required" && (
                  <span className="validation-error">This is required</span>
                )}
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label>Status</label>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      {...register("status", { required: true })}
                      id="inlineRadio1"
                      value="1"
                      defaultChecked
                    />
                    <label className="form-check-label" htmlFor="inlineRadio1">
                      Active
                    </label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      {...register("status", { required: true })}
                      id="inlineRadio2"
                      value="0"
                    />
                    <label className="form-check-label" htmlFor="inlineRadio2">
                      Inactive
                    </label>
                  </div>
                </div>
                {errors.status && errors.status.type === "required" && (
                  <span className="validation-error">This is required</span>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <Button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                Save
              </Button>
            </div>
          </Form>

          <hr style={{ margin: "24px 0" }} />

          <div className="levels-section">
            <h5 className="modal-title" style={{ marginBottom: 12 }}>
              Levels
            </h5>
            <p style={{ fontSize: 13, color: "#64748b", marginBottom: 16 }}>
              Progressive difficulty tiers for this subject. Each level belongs
              only to this subject — the same name in another subject is a
              separate level.
            </p>

            <form
              onSubmit={handleAddLevel}
              style={{
                display: "flex",
                gap: 8,
                marginBottom: 16,
              }}
            >
              <input
                type="text"
                className="form-control"
                placeholder="Level name (e.g. Enrich Maths)"
                value={newLevel}
                onChange={(e) => setNewLevel(e.target.value)}
                disabled={levelsBusy}
                style={{ flex: 1 }}
              />
              <input
                type="text"
                className="form-control"
                placeholder="Abbreviation (e.g. L4)"
                value={newLevelAbbr}
                onChange={(e) => setNewLevelAbbr(e.target.value)}
                disabled={levelsBusy}
                style={{ flex: 1 }}
              />
              <Button
                type="submit"
                className="btn btn-primary"
                disabled={levelsBusy || !newLevel.trim()}
              >
                Add
              </Button>
            </form>

            <table className="table table-bordered">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>No.</th>
                  <th>Level name</th>
                  <th>Abbreviation</th>
                  <th style={{ width: 180 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {levels.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      style={{ textAlign: "center", color: "#94a3b8" }}
                    >
                      No levels yet. Add one above.
                    </td>
                  </tr>
                ) : (
                  levels.map((row, idx) => (
                    <tr key={row.id}>
                      <td>{idx + 1}</td>
                      <td>
                        {editingId === row.id ? (
                          <input
                            type="text"
                            className="form-control"
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleSaveEdit();
                              if (e.key === "Escape") handleCancelEdit();
                            }}
                            autoFocus
                            disabled={levelsBusy}
                          />
                        ) : (
                          row.name
                        )}
                      </td>
                      <td>
                        {editingId === row.id ? (
                          <input
                            type="text"
                            className="form-control"
                            value={editingAbbr}
                            onChange={(e) => setEditingAbbr(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleSaveEdit();
                              if (e.key === "Escape") handleCancelEdit();
                            }}
                            disabled={levelsBusy}
                          />
                        ) : (
                          row.abbreviation || (
                            <span style={{ color: "#cbd5e1" }}>—</span>
                          )
                        )}
                      </td>
                      <td>
                        {editingId === row.id ? (
                          <>
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              onClick={handleSaveEdit}
                              disabled={levelsBusy || !editingName.trim()}
                              style={{ marginRight: 6 }}
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-secondary"
                              onClick={handleCancelEdit}
                              disabled={levelsBusy}
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="edit-btn"
                              onClick={() => handleStartEdit(row)}
                              style={{ marginRight: 10 }}
                              title="Edit"
                            >
                              <i
                                className="fa fa-pencil"
                                aria-hidden="true"
                              ></i>
                            </button>
                            <button
                              type="button"
                              className="edit-btn"
                              onClick={() => handleDeleteLevel(row.id)}
                              title="Delete"
                            >
                              <i
                                className="fa fa-trash"
                                aria-hidden="true"
                              ></i>
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export { AddSubject, EditSubject };
